// Conference Room Notes + Files panels — both anchored to
// sq_calendar_workspace_items (anchor_kind: 'meeting', anchor_id: meetingId),
// the SAME real, general-purpose notes/links/file-ref spine the Calendar
// workroom already uses for every other anchor kind (google_event, draft,
// plan, task, daily_meeting). Works for every meeting unconditionally — no
// portal pre-linking required. The backing migration (20260711000001) is
// source-ready; until an operator applies it, the route honestly returns
// backend_not_migrated and these panels show that state plainly rather than
// fabricating notes or files.

import { useCallback, useEffect, useState } from 'react'
import {
  addCalendarItem,
  removeCalendarItem,
  attachCalendarFile,
  openCalendarFile,
  deleteCalendarFile,
  humanizeCalendarError,
  isItemsMigrationError,
} from '../../calendar/calendarData'
import { api, isAuthError } from '../../../../api/client'
import type { CalendarWorkspaceItem } from '../../../../api/client'

const MONO = 'var(--sc-font-mono, "JetBrains Mono", ui-monospace, monospace)'

type ItemsState = {
  loading: boolean
  authRequired: boolean
  migrationRequired: boolean
  error: string | null
  items: CalendarWorkspaceItem[]
}

function useMeetingWorkspaceItems(meetingId: string, itemType: CalendarWorkspaceItem['item_type']) {
  const [state, setState] = useState<ItemsState>({ loading: true, authRequired: false, migrationRequired: false, error: null, items: [] })

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }))
    try {
      const res = await api.quamCalendar.workspaceItems.list({ anchor_kind: 'meeting', anchor_id: meetingId })
      setState({ loading: false, authRequired: false, migrationRequired: false, error: null, items: res.items.filter((i) => i.item_type === itemType) })
    } catch (err) {
      if (isAuthError(err)) { setState({ loading: false, authRequired: true, migrationRequired: false, error: null, items: [] }); return }
      if (isItemsMigrationError(err)) { setState({ loading: false, authRequired: false, migrationRequired: true, error: null, items: [] }); return }
      setState({ loading: false, authRequired: false, migrationRequired: false, error: humanizeCalendarError(err), items: [] })
    }
  }, [meetingId, itemType])

  useEffect(() => { void load() }, [load])
  return { ...state, reload: load }
}

function MigrationNotice() {
  return <p className="cr-state-note">This tab's storage table (sq_calendar_workspace_items) is source-ready but not yet applied in this environment — an operator must approve the migration before notes or files persist here.</p>
}

// ---- Notes -------------------------------------------------------------
export function MeetingNotesPanel({ meetingId }: { meetingId: string }) {
  const items = useMeetingWorkspaceItems(meetingId, 'note')
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const add = async () => {
    const body = draft.trim()
    if (!body || saving) return
    setSaving(true)
    try {
      await addCalendarItem({ anchor_kind: 'meeting', anchor_id: meetingId, item_type: 'note', body })
      setDraft('')
      await items.reload()
    } catch {
      // Honest no-op — the draft stays so the operator can retry.
    } finally {
      setSaving(false)
    }
  }

  if (items.authRequired) return <p className="cr-state-note">Sign in to read this meeting's notes.</p>
  if (items.migrationRequired) return <MigrationNotice />
  if (items.error) return <p className="cr-state-note">{items.error}</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ fontFamily: MONO, fontSize: 10, color: '#8a93a3', marginBottom: 8 }}>LIVE · notes anchored to this meeting</div>
      <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gap: 8, alignContent: 'start' }}>
        {items.loading && <p className="cr-state-note">Loading notes…</p>}
        {!items.loading && items.items.length === 0 && <p className="cr-state-note">No notes yet.</p>}
        {items.items.map((n) => (
          <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p style={{ margin: 0, fontSize: 12.5, color: '#e8ecf2', lineHeight: 1.5 }}>{n.body}</p>
            <button type="button" onClick={() => void removeCalendarItem(n.id).then(() => items.reload())} aria-label="Delete note" style={{ flex: '0 0 auto' }}>Delete</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void add() }}
          placeholder="Add a note…"
          aria-label="Add a meeting note"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.03)', color: '#e8ecf2', fontSize: 12.5 }}
        />
        <button type="button" onClick={() => void add()} disabled={saving || !draft.trim()}>Add</button>
      </div>
    </div>
  )
}

// ---- Files ---------------------------------------------------------------
export function MeetingFilesPanel({ meetingId }: { meetingId: string }) {
  const items = useMeetingWorkspaceItems(meetingId, 'file')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      await attachCalendarFile(file, { anchor_kind: 'meeting', anchor_id: meetingId })
      await items.reload()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  if (items.authRequired) return <p className="cr-state-note">Sign in to read files linked to this meeting.</p>
  if (items.migrationRequired) return <MigrationNotice />
  if (items.error) return <p className="cr-state-note">{items.error}</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <p style={{ fontSize: 10.5, color: '#5c6675', fontFamily: MONO, lineHeight: 1.6, margin: '0 0 8px' }}>Files upload to your own canonical storage; this list is meeting-linked references, not dedicated shared meeting storage.</p>
      <div style={{ flex: 1, overflowY: 'auto', display: 'grid', gap: 8, alignContent: 'start' }}>
        {items.loading && <p className="cr-state-note">Loading files…</p>}
        {!items.loading && items.items.length === 0 && <p className="cr-state-note">No files yet.</p>}
        {items.items.map((f) => (
          <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: 12, color: '#e8ecf2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name || f.title || 'file'}</span>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <button type="button" onClick={() => void openCalendarFile(f)}>Open</button>
              <button type="button" onClick={() => void deleteCalendarFile(f).then(() => items.reload())}>Delete</button>
            </span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: uploading ? 'default' : 'pointer', fontSize: 12, color: '#7fe6ff' }}>
          <input type="file" onChange={(e) => void onPick(e)} disabled={uploading} style={{ display: 'none' }} />
          {uploading ? 'Uploading…' : 'Upload a file'}
        </label>
        {uploadError && <p className="cr-state-note" style={{ color: '#ff7a7a' }}>{uploadError}</p>}
      </div>
    </div>
  )
}
